import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useState } from "react";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Form,
  FormGroup,
  Label,
  ListGroup,
  ListGroupItem,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
  Table,
  Input,
  Spinner,
  Navbar,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Badge,
  FormText,
} from "reactstrap";
import EnterpriseService from "../../common/services/enterprise-service";
import truckyService from "../../common/services/trucky-service";
import { toast, ToastContainer } from "react-toastify";
import { useNavigate, useParams } from "react-router";
import LoadingOverlay from "react-loading-overlay";
import MonthlyCompanyStatsInnerComponent from "./MonthlyCompanyStatsInnerComponent";
import classnames from "classnames";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import SingleCompanyAward from "./SingleCompanyAward";
import CustomBadge from "../ui/CustomBadge";
import { Link } from "react-router-dom";
import CompanyFeedItems from "./CompanyFeedItems";
import Paginator from "../ui/Paginator";
import BoxDynamicAd from "../../ads/BoxDynamicAd";
import CompanyMembers from "./CompanyMembers";
import LazyImage from "../../web/components/common/LazyImage";
import UpcomingCompanyEvents from "./UpcomingCompanyEvents";
import CompanyHero from "./CompanyHero";

const CompanyDetail = ({ container, close }) => {
  const es = new EnterpriseService();
  let { id, name } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [company, setCompany] = useState(null);
  const [activeTab, toggleTab] = useState("about");
  const [awards, setAwards] = useState([]);
  const [feeds, setFeeds] = useState([]);
  const [pinnedFeeds, setPinnedFeeds] = useState([]);
  const [feedsPage, setFeedsPage] = useState(1);
  const [feedsTotalPages, setFeedsTotalPage] = useState(0);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    getCompany();
  }, []);

  useEffect(() => {
    if (company != null) {
      getAwards();
      getPinnedFeeds();
      getNotPinnedFeeds();
    }
  }, [company]);

  useEffect(() => {
    if (company != null) {
      getNotPinnedFeeds();
    }
  }, [feedsPage]);

  useEffect(() => {
    if (feeds.length > 0 || pinnedFeeds.length > 0) {
      toggleTab("feeds");
    }
  }, [feeds.length, pinnedFeeds.length]);

  const getCompany = async () => {
    setLoading(true);
    let company = null;

    if (id) company = await es.getCompany(id);
    else company = await es.getCompanyBySlug(name);

    if (company && company.id) {
      setCompany(company);

      if (company.about == null) toggleTab("members");
    } else {
      setNotFound(true);
    }
    setLoading(false);
  };

  const getPinnedFeeds = () => {
    es.getPublicPublishedFeed(company.id, { pinned: "onlyPinned" }).then(
      setPinnedFeeds
    );
  };

  const getNotPinnedFeeds = async () => {
    const result = await es.getPublicPublishedFeed(company.id, {
      paginate: true,
      pinned: "notPinned",
      page: feedsPage,
    });
    setFeeds(result.data);
    setFeedsTotalPage(result.last_page);
  };

  const getAwards = async () => {
    const result = await es.getCompanyAwards(company);
    setAwards(result);
  };

  const handlePageClick = (event) => {
    setFeedsPage(event.selected + 1);
  };

  const getCompanyTypeLabel = () => {
    if (company.company_type === "realistic") return container.localeManager.strings.realisticEconomy;
    if (company.company_type === "miles") return container.localeManager.strings.enterpriseMiles;
    if (company.company_type === "both") return container.localeManager.strings.ecoAndMiles;
    return null;
  };

  const getCompanyInfoBox = () => {
    const socials = [
      { key: "discord",  icon: ["fab", "discord"],          cls: "text-discord",  title: "Discord"  },
      { key: "facebook", icon: ["fab", "facebook-square"],  cls: "text-facebook", title: "Facebook" },
      { key: "twitter",  icon: ["fab", "twitter-square"],   cls: "text-twitter",  title: "Twitter"  },
      { key: "youtube",  icon: ["fab", "youtube-square"],   cls: "text-youtube",  title: "YouTube"  },
      { key: "twitch",   icon: ["fab", "twitch"],           cls: "text-twitch",   title: "Twitch"   },
    ].filter((s) => company[s.key] != null);

    const typeLabel = getCompanyTypeLabel();

    return (
      <Card className="company-info-box">
        <CardBody className="p-0">
          <div className="company-info-rows">
            <div className="company-info-row">
              <span className="company-info-label">{container.localeManager.strings.owner}</span>
              <Link to={`/user/${company.owner.id}`} className="company-info-value text-white">
                {company.owner.name}
              </Link>
            </div>
            {company.language != null && (
              <div className="company-info-row">
                <span className="company-info-label">{container.localeManager.strings.settingsHeaderLanguage}</span>
                <span className="company-info-value">{company.language}</span>
              </div>
            )}
            {company.tag != null && (
              <div className="company-info-row">
                <span className="company-info-label">{container.localeManager.strings.tag}</span>
                <span className="company-info-value">[{company.tag}]</span>
              </div>
            )}
            {typeLabel != null && (
              <div className="company-info-row">
                <span className="company-info-label">{container.localeManager.strings.companyStyle}</span>
                <span className="company-info-value">{typeLabel}</span>
              </div>
            )}
            {company.members_count != null && (
              <div className="company-info-row">
                <span className="company-info-label">{container.localeManager.strings.members}</span>
                <span className="company-info-value">{company.members_count}</span>
              </div>
            )}
          </div>

          {socials.length > 0 && (
            <div className="company-info-socials">
              {socials.map((s) => (
                <a
                  key={s.key}
                  title={s.title}
                  className={`company-info-social-btn ${s.cls}`}
                  onClick={() => truckyService.openUrlInDefaultBrowser(company[s.key])}
                >
                  <FontAwesomeIcon icon={s.icon} className="mx-0" />
                </a>
              ))}
            </div>
          )}

          {company.website != null && (
            <div className="company-info-website">
              <FontAwesomeIcon icon="link" className="me-2 flex-shrink-0 opacity-50" />
              <a
                className="company-info-website-link"
                onClick={() => truckyService.openUrlInDefaultBrowser(company.website)}
                title={company.website}
              >
                {company.website.replace(/^https?:\/\/(www\.)?/, "")}
              </a>
            </div>
          )}

          {company.recruitment === "open" && truckyService.getToken() && (
            <div className="p-3">
              <Link to={`/vtc/${company.slug}/apply`} className="btn btn-primary w-100">
                <FontAwesomeIcon icon="hand-point-right" className="me-2" />
                {container.localeManager.strings.applyNow}
              </Link>
            </div>
          )}
        </CardBody>
      </Card>
    );
  };

  return (
    <LoadingOverlay
      active={loading}
      spinner={
        <Spinner
          color="primary"
          style={{ width: "4rem", height: "4rem" }}
        ></Spinner>
      }
    >
      <Row noGutters className="d-flex flex-nowrap">
        <Col>
          <Card className="h-100">
            <CardBody>
              {notFound && (
                <div className="alert alert-danger">Company not Found</div>
              )}
              {company != null && (
                <>
                  <Row noGutters>
                    <Col>
                      <CompanyHero company={company} />
                      <div className="d-md-none my-3">
                        {getCompanyInfoBox()}
                      </div>

                      <div className="my-3">
                        <MonthlyCompanyStatsInnerComponent
                          company={company}
                          container={container}
                          enableNavigation={true}
                          detailedMembersStats={false}
                          detailedJobsStats={false}
                        ></MonthlyCompanyStatsInnerComponent>
                      </div>
                      <UpcomingCompanyEvents
                        company_id={company.id}
                        container={container}
                        onlyPrivate={false}
                        limit={3}
                      />
                      <>
                        <Nav
                          className="nav-custom nav-line-tabs nav-line-tabs-2x fs-6 fw-bold mb-3"
                          tabs
                        >
                          {(feeds.length > 0 || pinnedFeeds.length > 0) && (
                            <NavItem>
                              <NavLink
                                className={classnames({
                                  active: activeTab === "feeds",
                                })}
                                onClick={() => {
                                  toggleTab("feeds");
                                }}
                              >
                                <FontAwesomeIcon icon="newspaper" className="me-2" />
                                {container.localeManager.strings.newsFeed}
                              </NavLink>
                            </NavItem>
                          )}
                          {company.about != null && (
                            <NavItem>
                              <NavLink
                                className={classnames({
                                  active: activeTab === "about",
                                })}
                                onClick={() => {
                                  toggleTab("about");
                                }}
                              >
                                <FontAwesomeIcon icon="circle-info" className="me-2" />
                                {container.localeManager.strings.about}
                              </NavLink>
                            </NavItem>
                          )}
                          {company.rules != null && (
                            <NavItem>
                              <NavLink
                                className={classnames({
                                  active: activeTab === "rules",
                                })}
                                onClick={() => {
                                  toggleTab("rules");
                                }}
                              >
                                <FontAwesomeIcon icon="scale-balanced" className="me-2" />
                                {container.localeManager.strings.rules}
                              </NavLink>
                            </NavItem>
                          )}
                          {company.requirements != null && (
                            <NavItem>
                              <NavLink
                                className={classnames({
                                  active: activeTab === "requirements",
                                })}
                                onClick={() => {
                                  toggleTab("requirements");
                                }}
                              >
                                <FontAwesomeIcon icon="clipboard-check" className="me-2" />
                                {container.localeManager.strings.requirements}
                              </NavLink>
                            </NavItem>
                          )}
                          <NavItem>
                            <NavLink
                              className={classnames({
                                active: activeTab === "members",
                              })}
                              onClick={() => {
                                toggleTab("members");
                              }}
                            >
                              <FontAwesomeIcon icon="users" className="me-2" />
                              {container.localeManager.strings.members}{" "}
                              <CustomBadge color="primary" className="ms-3">
                                {company.members_count}
                              </CustomBadge>
                            </NavLink>
                          </NavItem>
                          {awards.length > 0 && (
                            <NavItem>
                              <NavLink
                                className={classnames({
                                  active: activeTab === "awards",
                                })}
                                onClick={() => {
                                  toggleTab("awards");
                                }}
                              >
                                <FontAwesomeIcon icon="award" className="me-2" />
                                {container.localeManager.strings.companyAwards}{" "}
                                <CustomBadge color="primary" className="ms-3">
                                  {awards.length}
                                </CustomBadge>
                              </NavLink>
                            </NavItem>
                          )}
                        </Nav>
                        <TabContent className="mt-3" activeTab={activeTab}>
                          {company.about != null && (
                            <TabPane tabId="about">
                              <Row>
                                <Col>
                                  <ReactMarkdown
                                    children={company.about}
                                    remarkPlugins={[remarkGfm]}
                                  />
                                </Col>
                              </Row>
                            </TabPane>
                          )}
                          {(feeds.length > 0 || pinnedFeeds.length > 0) && (
                            <TabPane tabId="feeds">
                              <Row>
                                <Col>
                                  {pinnedFeeds.length > 0 && (
                                    <CompanyFeedItems
                                      container={container}
                                      items={pinnedFeeds}
                                    ></CompanyFeedItems>
                                  )}
                                  {feeds.length > 0 && (
                                    <div className="my-3">
                                      <Paginator
                                        forcePage={feedsPage - 1}
                                        totalPages={feedsTotalPages}
                                        handlePageClick={handlePageClick}
                                      />
                                      <CompanyFeedItems
                                        container={container}
                                        items={feeds}
                                      ></CompanyFeedItems>
                                      <Paginator
                                        forcePage={feedsPage - 1}
                                        totalPages={feedsTotalPages}
                                        handlePageClick={handlePageClick}
                                      />
                                    </div>
                                  )}
                                </Col>
                              </Row>
                            </TabPane>
                          )}
                          {company.requirements != null && (
                            <TabPane tabId="requirements">
                              <Row>
                                <Col>
                                  <ReactMarkdown
                                    children={company.requirements}
                                    remarkPlugins={[remarkGfm]}
                                  />
                                </Col>
                              </Row>
                            </TabPane>
                          )}
                          {company.rules != null && (
                            <TabPane tabId="rules">
                              <Row>
                                <Col>
                                  <ReactMarkdown
                                    children={company.rules}
                                    remarkPlugins={[remarkGfm]}
                                  />
                                </Col>
                              </Row>
                            </TabPane>
                          )}
                          {activeTab == "members" && company != null && (
                            <TabPane tabId="members">
                              <CompanyMembers
                                container={container}
                                company={company}
                              ></CompanyMembers>
                            </TabPane>
                          )}
                          <TabPane tabId="awards">
                            <Row className="g-3">
                              {awards.map((m) => (
                                <Col key={m.id} xs={12} md={6} xl={4}>
                                  <SingleCompanyAward
                                    container={container}
                                    award={m}
                                  />
                                </Col>
                              ))}
                            </Row>
                          </TabPane>
                        </TabContent>
                      </>
                    </Col>
                  </Row>
                </>
              )}
            </CardBody>
          </Card>
        </Col>
        <aside className="ad-column-placeholder d-none d-md-block">
          {company != null && getCompanyInfoBox()}
          {!window.overwolf && (
            <BoxDynamicAd container={container} className="my-3" />
          )}
        </aside>
      </Row>
    </LoadingOverlay>
  );
};

export default CompanyDetail;
